import { defineStore } from 'pinia'
import { useStoreAuth } from '@/stores/storeAuth'
import { useStoreFeed } from '@/stores/storeFeed'
import { useStoreHomeFeed } from '@/stores/storeHomeFeed'
import { useUIPreferences } from '@/composables/useUIPreferences'
import { supabase } from '@/supabase'

let storeAuth = null
let storeFeed = null
let storeHomeFeed = null

// feature_flags keys exposed via the Developer Preferences section in
// ViewSettingsAdvanced.vue. Restricted to admin users; reset on non-admins
// so any prior toggling reverts to default behavior.
const DEV_PREFERENCE_FEATURE_FLAGS = [
  'show_on_deck_section',
  'title_card_stacks_enabled',
  'review_card_stacks_enabled',
  'explore_reviews_scrollable',
  'reactions_enabled',
  'tiers_enabled',
  'fullscreen_modals'
]

export const useStoreProfile = defineStore('storeProfile', {
  persist: {
    // Only persist the current user ID - profile data is fetched fresh
    // Removed users, follows, following to prevent localStorage quota issues
    pick: ['currentUserId'],
    serializer: {
      serialize: JSON.stringify,
      deserialize: JSON.parse
    }
  },
  state: () => {
    return {
      users: [],
      follows: [],
      following: [],
      loaded: false,
      loading: false,
      preferences: {},
      currentUserId: null,
      currentUserLoaded: false,
      currentUserHasAccess: true,
      currentUserIsAdmin: false,
      currentUserNeedsOnboarding: false,
      currentUserPendingDeletion: false,
      currentSessionSeenWelcome: false,
      currentSessionSeenOnboarding: false,
      // Error tracking for profile fetch
      fetchError: null,
      lastFetchedAt: null
    }
  },
  actions: {
    async init() {
      storeAuth = useStoreAuth()
      storeFeed = useStoreFeed()
      storeHomeFeed = useStoreHomeFeed()

      // Set the base session data back to inital
      this.currentSessionSeenWelcome = false
      this.currentSessionSeenOnboarding = false
      this.currentUserPendingDeletion = false

      // Only fetch profile data if user is authenticated
      if (storeAuth.user) {
        await this.fetch()
      } else {
        console.log('Skipping profile fetch - user not authenticated')
      }
    },

    async setWelcomeComplete() {
      // Send the update to the server first
      const path = `profile/setNeedsWelcome`
      const { data, error } = await supabase.functions.invoke(path)
      if (error) {
        console.error('Failed setting welcome seen', error)
        return
      }

      // Fetch updated profile to get the current needs_onboarding state
      await this.fetchCurrentUserProfile()

      // If user still needs onboarding, reset session flag to allow onboarding modal
      if (this.needsOnboarding) {
        this.currentSessionSeenWelcome = false
      } else {
        this.currentSessionSeenWelcome = true
      }

      return
    },

    async setOnboardingComplete() {
      // Mark both flags as true - user has completed the full welcome/onboarding flow
      this.currentSessionSeenOnboarding = true
      this.currentSessionSeenWelcome = true

      // Send the update to the server
      const path = `profile/setNeedsOnboarding`
      const { data, error } = await supabase.functions.invoke(path, {
        body: {
          needs_onboarding: false,
          is_onboarded: true
        }
      })
      if (error) {
        console.error('Failed setting welcome seen', error)
        return
      }

      // Update local state to reflect onboarding completion
      const currentUser = this.users.find(
        (user) => user.user_id === this.currentUserId
      )
      if (currentUser) {
        currentUser.needs_onboarding = false
        currentUser.is_onboarded = true
      }

      return
    },

    async updateProfile(newData) {
      const path = `profile/update`
      const { error } = await supabase.functions.invoke(path, {
        body: { ...newData }
      })
      if (error) {
        const errBody = await new Response(error.context.body).json()
        throw errBody
      }

      await this.fetchCurrentUserProfile()
      return true
    },

    // Add this method to your storeProfile.js file
    async updateAvatar(avatarData) {
      try {
        // For image uploads, we need to handle the file separately
        if (avatarData.avatarType === 'image' && avatarData.avatarFile) {
          // Create FormData for file upload
          const formData = new FormData()

          // Add original file if provided
          if (avatarData.originalFile) {
            formData.append('originalFile', avatarData.originalFile)
          }

          // Add regular sized avatar (required)
          formData.append('avatarFile', avatarData.avatarFile)

          // Add small sized avatar if provided
          if (avatarData.avatarFileSm) {
            formData.append('avatarFileSm', avatarData.avatarFileSm)
          }

          // Call the updateAvatar endpoint with FormData
          const { error, data } = await supabase.functions.invoke(
            'profile/updateAvatar',
            {
              body: formData,
              headers: {
                // Don't set Content-Type, let the browser set it with the boundary
                Accept: 'application/json'
              }
            }
          )

          if (error) {
            const errBody = await new Response(error.context.body).json()
            throw errBody
          }

          // Refresh user profile to get the updated data
          await this.fetchCurrentUserProfile()
          return data
        }

        // Only image uploads are supported now
        throw new Error('Avatar uploads must include an image file')
      } catch (error) {
        console.error('Error updating avatar:', error)
        throw error
      }
    },

    async fetchCurrentUserProfile() {
      return await this.fetchUserProfile(this.currentUserId)
    },

    async fetch() {
      if (!storeAuth.user) return
      this.currentUserId = storeAuth.user.id
      this.loading = true
      this.fetchError = null

      try {
        const user = await this.fetchCurrentUserProfile()

        // Check if user has requested account deletion
        if (user?.deletion_requested_at || user?.is_deleted) {
          this.currentUserPendingDeletion = true
          this.currentUserHasAccess = false
          this.currentUserLoaded = true
          this.loading = false
          // Don't load any other data for deleted users
          return
        }

        // Fetch social data in parallel (non-blocking for the profile to be ready)
        // These are not critical for initial render
        Promise.all([
          this.fetchUserFollows(storeAuth.user.id),
          this.fetchUserFollowing(storeAuth.user.id)
        ]).catch((err) => {
          console.warn('Failed to fetch social data:', err)
        })

        if (user?.is_admin === true) {
          this.currentUserHasAccess = true
          this.currentUserIsAdmin = true
          this.currentUserNeedsOnboarding = false
        } else {
          this.resetDevPreferencesForNonAdmin().catch((err) => {
            console.warn('Failed to reset dev preferences:', err)
          })
        }

        this.currentUserLoaded = true
        this.lastFetchedAt = Date.now()
      } catch (error) {
        console.error('Failed to fetch user profile:', error)
        // If profile fetch fails entirely, mark as loaded but with no access
        this.currentUserLoaded = true
        this.currentUserHasAccess = false
        this.fetchError = error?.message || 'Failed to load profile'
      } finally {
        this.loading = false
      }
    },

    // Force refresh profile data - clears error state and retries
    async refresh() {
      this.fetchError = null
      await this.fetch()
    },

    async fetchUserFollowing(userId) {
      // Guard: Validate userId parameter
      // This fetches users who FOLLOW the current user (followers list)
      if (!userId) {
        console.warn('fetchUserFollowing called with undefined userId')
        return
      }

      const { data, error } = await supabase
        .from('following_relationships')
        .select('*, profiles!fk_user(*, reviews(count))')
        .eq('profiles.reviews.status', 'published')
        .eq('profiles.reviews.is_deleted', false)
        .eq('following_user_id', userId)
        .neq('user_id', userId)

      if (error) {
        console.error('Fetching user followers errored ', error)
        return
      }

      this.following = data
    },
    async fetchUserFollows(userId) {
      // Guard: Validate userId parameter
      // This fetches users that the current user FOLLOWS (excluding self-follow)
      if (!userId) {
        console.warn('fetchUserFollows called with undefined userId')
        return
      }

      const { data, error } = await supabase
        .from('following_relationships')
        .select('*, profiles!fk_following_user(*, reviews(count))')
        .eq('profiles.reviews.status', 'published')
        .eq('profiles.reviews.is_deleted', false)
        .eq('user_id', userId)
        .neq('following_user_id', userId)

      if (error) {
        console.error('Fetching user follows errored ', error)
        return
      }

      this.follows = data
    },

    // Populate badge data on a profile object
    async _populateBadges(profile) {
      if (!profile) return profile

      // Load status badge from the badges table via status_badge_id
      if (profile.status_badge_id) {
        try {
          const { data: badge } = await supabase
            .from('badges')
            .select('*')
            .eq('id', profile.status_badge_id)
            .single()
          profile.status_badge = badge || null
        } catch {
          profile.status_badge = null
        }
      } else {
        profile.status_badge = null
      }

      // Load achievement badges from user_badges
      try {
        const { data: userBadges } = await supabase
          .from('user_badges')
          .select('*, badges(*)')
          .eq('user_id', profile.user_id)
          .eq('is_revoked', false)

        profile.badges = (userBadges || [])
          .map((ub) => ub.badges)
          .filter(Boolean)
      } catch {
        profile.badges = []
      }

      return profile
    },

    async fetchUserProfile(userId) {
      // Guard: Validate userId parameter
      if (!userId) {
        console.warn('fetchUserProfile called with undefined userId')
        return null
      }

      const { data, error } = await supabase
        .from('profiles')
        .select(
          `*,
          reviews(count),
          user_review_stats(*),
          user_review_stats_by_type(*)`
        )
        .eq('reviews.status', 'published')
        .eq('reviews.is_deleted', false)
        .eq('user_id', userId)
        .limit(1)
        .single()

      if (error) throw error

      if (data) {
        await this._populateBadges(data)
      }

      this.updateUsers([data])
      return data
    },

    async fetchUserProfileByUsername(username) {
      if (username === undefined) return

      const { data, error } = await supabase
        .from('profiles')
        .select(
          `*,
          reviews(count),
          user_review_stats(*),
          user_review_stats_by_type(*)`
        )
        .eq('reviews.status', 'published')
        .eq('reviews.is_deleted', false)
        .eq('username', username)
        .limit(1)
        .single()

      if (error) throw error

      if (data) {
        await this._populateBadges(data)
      }

      this.updateUsers([data])
    },

    async fetchProfileById(userId) {
      if (!userId) return null

      // Check if we already have the user in the store
      const existingUser = this.users.find((user) => user.user_id === userId)
      if (existingUser) return existingUser

      const { data, error } = await supabase
        .from('profiles')
        .select('user_id, username, displayname, avatar_url')
        .eq('user_id', userId)
        .single()

      if (error) {
        console.error('Failed to fetch profile by ID:', error)
        return null
      }

      if (data) {
        // Add minimal data to store
        this.updateUsers([data])
      }

      return data
    },

    async followUser(userId) {
      const path = `users/${userId}/follow`
      const { data, error } = await supabase.functions.invoke(path)

      if (error) {
        console.error('Failed following user', error)
        return { success: false, error }
      }

      // Track user follow in PostHog
      if (import.meta.env.VITE_APP_MODE === 'production') {
        import('@/composables/usePostHog').then(({ usePostHog }) => {
          const { track } = usePostHog()
          track('User Followed', {
            followed_user_id: userId
          })
        })
      }

      await this.fetchUserFollows(storeAuth.user.id)
      storeFeed.fetch() // Update the user's feed
      storeHomeFeed?.fetchInBackground() // Update home feed reviews
      return { success: true, data }
    },

    async unfollowUser(userId) {
      const path = `users/${userId}/unfollow`
      const { data, error } = await supabase.functions.invoke(path)

      if (error) {
        console.error('Failed unfollowing user', error)
        return { success: false, error }
      }

      // Track user unfollow in PostHog
      if (import.meta.env.VITE_APP_MODE === 'production') {
        import('@/composables/usePostHog').then(({ usePostHog }) => {
          const { track } = usePostHog()
          track('User Unfollowed', {
            unfollowed_user_id: userId
          })
        })
      }

      await this.fetchUserFollows(storeAuth.user.id)
      storeFeed.clear()
      storeFeed.fetch() // Update the user's feed
      storeHomeFeed?.fetchInBackground() // Update home feed reviews
      return { success: true, data }
    },

    async updateWatchProviders(regions, providers) {
      const path = `profile/updateWatchProviders`
      const { data, error } = await supabase.functions.invoke(path, {
        body: { regions, providers }
      })
      if (error) {
        const errBody = await new Response(error.context.body).json()
        throw errBody
      }

      await this.fetchCurrentUserProfile()
      return data
    },

    async updateUsername(username) {
      const path = `profile/updateUsername`
      const { data, error } = await supabase.functions.invoke(path, {
        body: { username }
      })
      if (error) {
        const errBody = await new Response(error.context.body).json()
        throw errBody
      }

      await this.fetchCurrentUserProfile()
      return data
    },

    async requestAccountDeletion(reason = null) {
      const path = `profile/requestDeletion`
      const { error } = await supabase.functions.invoke(path, {
        body: { reason }
      })
      if (error) {
        const errBody = await new Response(error.context.body).json()
        throw errBody
      }
      return true
    },

    async updatePinnedList(listId) {
      const path = `profile/updatePinnedList`
      const { data, error } = await supabase.functions.invoke(path, {
        body: { listId }
      })
      if (error) {
        const errBody = await new Response(error.context.body).json()
        throw errBody
      }

      // Update local state
      const currentUser = this.users.find(
        (user) => user.user_id === this.currentUserId
      )
      if (currentUser) {
        currentUser.pinned_list_id = listId || null
      }

      return data
    },

    async updateFeatureFlag(flagName, value) {
      if (!this.currentUserId) {
        throw new Error('No user logged in')
      }

      // Get current feature flags
      const currentUser = this.users.find(
        (user) => user.user_id === this.currentUserId
      )
      const currentFlags = currentUser?.feature_flags || {}

      // Merge the new flag
      const updatedFlags = {
        ...currentFlags,
        [flagName]: value
      }

      // Update in database
      const { error } = await supabase
        .from('profiles')
        .update({ feature_flags: updatedFlags })
        .eq('user_id', this.currentUserId)

      if (error) {
        console.error('Failed to update feature flag:', error)
        throw error
      }

      // Update local state
      if (currentUser) {
        currentUser.feature_flags = updatedFlags
      }

      return true
    },

    async resetDevPreferencesForNonAdmin() {
      if (!this.currentUserId) return

      const currentUser = this.users.find(
        (user) => user.user_id === this.currentUserId
      )
      if (!currentUser || currentUser.is_admin === true) return

      const currentFlags = currentUser.feature_flags || {}
      const flagsToStrip = DEV_PREFERENCE_FEATURE_FLAGS.filter(
        (key) => key in currentFlags
      )
      const clubsNeedsReset = currentUser.clubs_enabled === true

      // Always reset localStorage UI prefs — they are device-local and cheap
      // to overwrite, so no need to read first.
      const { resetDevPreferencesToDefaults } = useUIPreferences()
      resetDevPreferencesToDefaults()

      if (flagsToStrip.length === 0 && !clubsNeedsReset) return

      const updates = {}
      let cleanedFlags = currentFlags
      if (flagsToStrip.length > 0) {
        cleanedFlags = { ...currentFlags }
        for (const key of flagsToStrip) {
          delete cleanedFlags[key]
        }
        updates.feature_flags = cleanedFlags
      }
      if (clubsNeedsReset) {
        updates.clubs_enabled = false
      }

      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('user_id', this.currentUserId)

      if (error) {
        console.error('Failed to reset dev preferences for non-admin:', error)
        return
      }

      if (flagsToStrip.length > 0) currentUser.feature_flags = cleanedFlags
      if (clubsNeedsReset) currentUser.clubs_enabled = false
    },

    async updateClubsEnabled(value) {
      if (!this.currentUserId) {
        throw new Error('No user logged in')
      }

      // Update in database
      const { error } = await supabase
        .from('profiles')
        .update({ clubs_enabled: value })
        .eq('user_id', this.currentUserId)

      if (error) {
        console.error('Failed to update clubs_enabled:', error)
        throw error
      }

      // Update local state
      const currentUser = this.users.find(
        (user) => user.user_id === this.currentUserId
      )
      if (currentUser) {
        currentUser.clubs_enabled = value
      }

      return true
    },

    updateUsers(fetchedUsers) {
      // Create a map from the existing users array for quick lookup
      // Use user_id as key since it's always present (id may be missing
      // from minimal fetches like fetchProfileById)
      const itemsMap = new Map(
        this.users.map((item) => [item.user_id, item])
      )
      fetchedUsers.forEach((fetchedUser) => {
        if (itemsMap.has(fetchedUser.user_id)) {
          // Merge into the existing profile
          Object.assign(itemsMap.get(fetchedUser.user_id), fetchedUser)
        } else {
          // Insert the new profile
          itemsMap.set(fetchedUser.user_id, fetchedUser)
        }
      })
      // Update the users state with the updated array
      this.users = Array.from(itemsMap.values())
    }
  },
  getters: {
    isCurrentUserAdmin: (state) => {
      if (state.currentUserId === null) return false
      let user = state.users.find(
        (user) => user.user_id === state.currentUserId
      )

      if (!user) return false
      return user.is_admin
    },
    currentUsername: (state) => {},
    getCurrentUserProfile: (state) => {
      if (state.currentUserId === null) return null
      return state.users.find((user) => user.user_id === state.currentUserId)
    },
    getProfile: (state) => {
      return (userId) => {
        const profile = state.users.find((user) => user.user_id === userId)
        return profile
      }
    },

    getProfileByUsername: (state) => {
      return (username) => {
        return state.users.find((user) => user.username === username)
      }
    },

    username: (state) => {
      return (userId) => {
        return state.users.find((user) => user.id === userId)?.username || ''
      }
    },

    needsWelcome: (state) => {
      // If not loaded, return false
      // if loaded, check if user has a needs_welcome flag
      const currentUser = state.users.find(
        (user) => user.user_id === state.currentUserId
      )
      return currentUser?.needs_welcome || false
    },

    needsOnboarding: (state) => {
      // If not loaded, return false
      // if loaded, check if user has a needs_onboarding flag
      const currentUser = state.users.find(
        (user) => user.user_id === state.currentUserId
      )
      return currentUser?.needs_onboarding || false
    },

    isOnboarded: (state) => {
      // Returns true if user has completed onboarding at least once
      const currentUser = state.users.find(
        (user) => user.user_id === state.currentUserId
      )
      return currentUser?.is_onboarded || false
    },
    isFollowingUser: (state) => {
      return (id) => {
        return state.follows.some(
          (relationship) => relationship.following_user_id == id
        )
      }
    },

    followingState: (state) => {
      return (id) => {
        return state.follows.find(
          (relationship) => relationship.following_user_id == id
        )
      }
    },

    pinnedListId: (state) => {
      const currentUser = state.users.find(
        (user) => user.user_id === state.currentUserId
      )
      return currentUser?.pinned_list_id || null
    },

    // Reactions preference (defaults to false - opt-in)
    reactionsEnabled: (state) => {
      const currentUser = state.users.find(
        (user) => user.user_id === state.currentUserId
      )
      return currentUser?.feature_flags?.reactions_enabled === true
    },

    // First-run checklist - returns true if user needs to see the checklist
    needsFirstRunChecklist: (state) => {
      const currentUser = state.users.find(
        (user) => user.user_id === state.currentUserId
      )
      if (!currentUser) return false

      // Only show if user is onboarded
      if (!currentUser.is_onboarded) return false

      // Check if checklist has been dismissed
      const checklistState = currentUser.feature_flags?.first_run_checklist
      if (checklistState?.dismissed_at) return false

      return true
    }
  }
})
